// Login.jsx
import { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../../context/TenantContext';
import '../../styles/Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshTenant } = useTenant();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Authenticate user
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Get user document
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (!userDoc.exists()) {
        throw new Error('No account found for this email.');
      }

      const userData = userDoc.data();

      // Verify user is active
      if (!userData.isActive) {
        throw new Error('Your account is deactivated. Please contact your administrator.');
      }

      // Refresh tenant context if user has tenant
      if (userData.tenantId) {
        await refreshTenant(userData.tenantId);
      }
      
      // Store session data in sessionStorage
      sessionStorage.setItem('authToken', userCredential.user.accessToken);
      sessionStorage.setItem('userRole', userData.role);
      sessionStorage.setItem('userId', userCredential.user.uid);
      if (userData.tenantId) sessionStorage.setItem('tenantId', userData.tenantId);
      if (userData.location) sessionStorage.setItem('location', userData.location);
      
      // Redirect based on role and previous location
      const from = location.state?.from?.pathname || (
        userData.role === 'super-admin' || userData.role === 'tenant-admin' 
          ? '/admin' 
          : userData.location ? '/location-admin' : '/'
      );
      
      navigate(from, { replace: true });

    } catch (error) {
      console.error('Login error:', error);
      setError(
        error.code === 'auth/wrong-password' ? 'Invalid email or password' :
        error.code === 'auth/user-not-found' ? 'User not found' :
        error.message.replace(/^Firebase:\s*/i, '')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="./teampresence-logo.png" alt="Admin Logo" className="admin-logo" />
          <h1>AV Checklist Admin Portal</h1>
          <p>Sign in to your account</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" disabled={loading} className="login-button">
            {loading ? (
              <>
                <span className="spinner"></span>
                Signing In...
              </>
            ) : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>Don't have an account? Contact your administrator</p>
        </div>
      </div>
    </div>
  );
};

export default Login;